import { UPLOAD_STATUS } from "../constants/uploader";

import type {
  UploadJob,
  UploadJobStatus,
  UploadQueueTarget,
} from "./uploadQueue.types";
import type { UploadStatus } from "../constants/uploader";

export type UploadQueueAction =
  | { type: "ENQUEUE"; job: UploadJob }
  | { type: "START"; jobId: string }
  | { type: "PROGRESS"; jobId: string; progress: number }
  | { type: "COMPLETE"; jobId: string; uploadedFilename: string | null }
  | { type: "ERROR"; jobId: string }
  | { type: "RETRY"; jobId: string }
  | { type: "REMOVE"; jobId: string };

export function uploadQueueReducer(
  state: UploadJob[],
  action: UploadQueueAction,
): UploadJob[] {
  switch (action.type) {
    case "ENQUEUE":
      // Enforce "one job per scope": drop any stale, non-active job for the same
      // scope (e.g. a previous error/pending) so we never keep a retry-able
      // orphan, then append the new one.
      return [
        ...state.filter(
          (job) =>
            job.scopeId !== action.job.scopeId ||
            job.status === "uploading" ||
            job.status === "completed",
        ),
        action.job,
      ];
    case "START":
      return state.map((job) =>
        job.id === action.jobId
          ? { ...job, status: "uploading", progress: 0 }
          : job,
      );
    case "PROGRESS":
      return state.map((job) =>
        job.id === action.jobId ? { ...job, progress: action.progress } : job,
      );
    case "COMPLETE":
      return state.map((job) =>
        job.id === action.jobId
          ? {
              ...job,
              status: "completed",
              progress: 100,
              uploadedFilename: action.uploadedFilename ?? job.uploadedFilename,
            }
          : job,
      );
    case "ERROR":
      return state.map((job) =>
        job.id === action.jobId
          ? { ...job, status: "error", progress: 0 }
          : job,
      );
    case "RETRY":
      return state.map((job) =>
        job.id === action.jobId
          ? { ...job, status: "pending", progress: 0 }
          : job,
      );
    case "REMOVE":
      return state.filter((job) => job.id !== action.jobId);
    default:
      return state;
  }
}

export function getActiveJob(jobs: UploadJob[]): UploadJob | undefined {
  return jobs.find((job) => job.status === "uploading");
}

export function getNextPendingJob(jobs: UploadJob[]): UploadJob | undefined {
  return jobs.find((job) => job.status === "pending");
}

export function getJobsForScope(
  jobs: UploadJob[],
  scopeId: string,
): UploadJob[] {
  return jobs.filter((job) => job.scopeId === scopeId);
}

/**
 * The job a page should currently reflect: the most recently enqueued one for
 * that scope. A completed job lingers (until acknowledged or purged), so a fresh
 * upload to the same scope is appended last — returning the last job keeps the
 * panel in sync with the new upload instead of the stale completed one.
 */
export function getCurrentJobForScope(
  jobs: UploadJob[],
  scopeId: string,
): UploadJob | undefined {
  const scopeJobList = getJobsForScope(jobs, scopeId);

  return scopeJobList[scopeJobList.length - 1];
}

export function buildAdvertisingVideoScopeId(
  campaignUuid: string,
  languageUuid: string,
): string {
  return `advertising:${campaignUuid}:${languageUuid}`;
}

/**
 * Single source of truth for the scope key format. The advertising prefix keeps
 * a campaign/language pair from ever colliding with a raw media uuid.
 */
export function buildScopeId(target: UploadQueueTarget): string {
  return target.kind === "advertisingVideo"
    ? buildAdvertisingVideoScopeId(target.campaignUuid, target.languageUuid)
    : target.mediaId;
}

export function mapJobStatusToUploadStatus(
  status: UploadJobStatus,
): UploadStatus {
  switch (status) {
    case "pending":
    case "uploading":
      return UPLOAD_STATUS.STARTED;
    case "completed":
      return UPLOAD_STATUS.COMPLETED;
    case "error":
      return UPLOAD_STATUS.ERROR;
    default: {
      // Exhaustiveness guard: a new UploadJobStatus must be mapped explicitly.
      const exhaustiveCheck: never = status;
      return exhaustiveCheck;
    }
  }
}
